package com.arena_web.ui.sections.eventdetail

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.arena_web.data.dto.EventParticipantDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.SportColors
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import kotlin.math.round

/**
 * 6-attribute mapping per sport — short FIFA-style display label paired
 * with the canonical `sport_attributes.id` UUID the backend expects in
 * `RatePlayerRequest.detailedRatings`. UUIDs follow the convention seeded
 * by the V8 migration: `20000000-0000-0000-<sportSlot>-<displayOrder>`.
 */
private data class RatingAttribute(val label: String, val attributeId: String)

private val FOOTBALL_ATTRIBUTES = listOf(
    RatingAttribute("PAC", "20000000-0000-0000-0001-000000000001"),
    RatingAttribute("SHO", "20000000-0000-0000-0001-000000000002"),
    RatingAttribute("PAS", "20000000-0000-0000-0001-000000000003"),
    RatingAttribute("DRI", "20000000-0000-0000-0001-000000000004"),
    RatingAttribute("DEF", "20000000-0000-0000-0001-000000000005"),
    RatingAttribute("PHY", "20000000-0000-0000-0001-000000000006"),
)
private val BASKETBALL_ATTRIBUTES = listOf(
    RatingAttribute("SCO", "20000000-0000-0000-0002-000000000001"),
    RatingAttribute("PAS", "20000000-0000-0000-0002-000000000002"),
    RatingAttribute("REB", "20000000-0000-0000-0002-000000000003"),
    RatingAttribute("DEF", "20000000-0000-0000-0002-000000000004"),
    RatingAttribute("SPD", "20000000-0000-0000-0002-000000000005"),
    RatingAttribute("IQ",  "20000000-0000-0000-0002-000000000006"),
)
private val TENNIS_ATTRIBUTES = listOf(
    RatingAttribute("SRV", "20000000-0000-0000-0003-000000000001"),
    RatingAttribute("FH",  "20000000-0000-0000-0003-000000000002"),
    RatingAttribute("BH",  "20000000-0000-0000-0003-000000000003"),
    RatingAttribute("VOL", "20000000-0000-0000-0003-000000000004"),
    RatingAttribute("FIT", "20000000-0000-0000-0003-000000000005"),
    RatingAttribute("TAC", "20000000-0000-0000-0003-000000000006"),
)
private val VOLLEYBALL_ATTRIBUTES = listOf(
    RatingAttribute("ATK", "20000000-0000-0000-0004-000000000001"),
    RatingAttribute("SRV", "20000000-0000-0000-0004-000000000002"),
    RatingAttribute("BLK", "20000000-0000-0000-0004-000000000003"),
    RatingAttribute("DEF", "20000000-0000-0000-0004-000000000004"),
    RatingAttribute("SET", "20000000-0000-0000-0004-000000000005"),
    RatingAttribute("FIT", "20000000-0000-0000-0004-000000000006"),
)
private val PADEL_ATTRIBUTES = listOf(
    RatingAttribute("SMA", "20000000-0000-0000-0005-000000000001"),
    RatingAttribute("VOL", "20000000-0000-0000-0005-000000000002"),
    RatingAttribute("DEF", "20000000-0000-0000-0005-000000000003"),
    RatingAttribute("POS", "20000000-0000-0000-0005-000000000004"),
    RatingAttribute("SRV", "20000000-0000-0000-0005-000000000005"),
    RatingAttribute("FIT", "20000000-0000-0000-0005-000000000006"),
)

private fun attributesForSport(sport: String): List<RatingAttribute> =
    when (sport.lowercase()) {
        "football" -> FOOTBALL_ATTRIBUTES
        "basketball" -> BASKETBALL_ATTRIBUTES
        "tennis" -> TENNIS_ATTRIBUTES
        "volleyball" -> VOLLEYBALL_ATTRIBUTES
        "padel" -> PADEL_ATTRIBUTES
        else -> FOOTBALL_ATTRIBUTES
    }

// Colours pulled straight from Figma 8691:151167 so the popup matches
// the design's exact palette rather than the running app's defaults.
private const val POPUP_BACKDROP = "rgba(2, 4, 11, 0.8)"
private const val POPUP_INNER_BG = "var(--l-s2, #09142d)" // list_client_row_hover
private const val PANEL_BG = "var(--l-hover2, #091a40)" // day-container_hover
private const val PANEL_BORDER = "rgba(60, 121, 241, 0.45)"
private const val STAR_GOLD = "#F2B73D"

/**
 * Rate Performance popup (Figma 8691:151167 / 8691:235407). Structure:
 *   - Outer fullscreen backdrop (rgba(2,4,11,0.8))
 *   - Inner container (bg var(--l-s2, #09142d), blue border, rounded 18, padding 56)
 *       - Left:  FIFA card preview
 *       - Right: Rating panel (its own card with bg var(--l-hover2, #091a40) + border)
 *           - Header / attribute rows / community rating / submit
 *   - Close (×) button anchored top-right of the inner container
 */
@Composable
fun RatePlayerPopup(
    player: EventParticipantDto,
    sport: String,
    // Existing per-attribute star counts the current viewer already
    // submitted for this player in this event (empty map = first-time
    // voter). Used to pre-fill the star UI so re-voting starts from the
    // user's earlier picks.
    initialRatings: Map<String, Int> = emptyMap(),
    // Average of all reviewers' generalRatings + the reviewer count —
    // surfaced in the community block at the bottom of the popup. Null
    // = no community ratings yet for this player in this event.
    communityAverage: Double? = null,
    communityCount: Int = 0,
    // Drives the spinner overlay + disables the submit/close handlers
    // while the rate POST is in flight.
    isSubmitting: Boolean = false,
    // `true` when the viewer is looking at their own card. Backend
    // rejects self-rating ("Cannot rate yourself"), so the submit
    // button is hidden — the popup is then read-only for inspecting
    // your community rating + per-attribute scores.
    isSelf: Boolean = false,
    // Tap on the FIFA card preview → navigate to the player's profile.
    // Defaults to a no-op so callers that don't wire profile navigation
    // (e.g. embeds) don't break.
    onViewProfile: (userId: String) -> Unit = {},
    onClose: () -> Unit,
    onSubmit: (Map<String, Int>) -> Unit,
) {
    val t = LocalAppStrings.current
    val attributes = remember(sport) { attributesForSport(sport) }
    // attributeId → star count (1..5). Initialised from the viewer's
    // previous review when one exists, otherwise all-zero.
    val ratings = remember(player.userId, sport, initialRatings) {
        mutableStateOf<Map<String, Int>>(
            attributes.associate { attr ->
                attr.attributeId to (initialRatings[attr.attributeId] ?: 0)
            }
        )
    }

    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .styleModifier {
                property("top", "0")
                property("left", "0")
                property("right", "0")
                property("bottom", "0")
                property("background", POPUP_BACKDROP)
                property("backdrop-filter", "blur(8px)")
                property("z-index", "2000")
            }
            .onClick { if (!isSubmitting) onClose() },
        contentAlignment = Alignment.Center,
    ) {
        // Inner container — the visible popup card. Self-contained so
        // clicks inside don't dismiss.
        Box(
            modifier = Modifier
                .position(Position.Relative)
                .sBorderRadius(18)
                .styleModifier {
                    property("background", POPUP_INNER_BG)
                }
                .onClick { it.stopPropagation() }
        ) {
            // Close (×) — top-right of the container, 44×44 outer hit
            // target, 36×36 circular pill inside per Figma 8691:235780.
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sSize(44)
                    .sTop(8)
                    .sRight(8)
                    .cursor(Cursor.Pointer)
                    .styleModifier { property("z-index", "2") }
                    .onClick {
                        it.stopPropagation()
                        onClose()
                    },
                contentAlignment = Alignment.Center,
            ) {
                Box(
                    modifier = Modifier
                        .sSize(36)
                        .sBorderRadius(100)
                        .styleModifier {
                            property("border", "0.3px solid var(--arena-border-top)")
                            property(
                                "background",
                                AppGradients.InteractiveSecondary
                            )
                        },
                    contentAlignment = Alignment.Center,
                ) {
                    SpanText(
                        "✕",
                        modifier = Modifier
                            .sFontSize(16)
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }
            }

            Row(
                modifier = Modifier
                    .styleModifier {
                        // Symmetric breathing space on all four edges
                        // so the card has visible room on its left,
                        // matching the right gap around the rating
                        // panel.
                        property(
                            "padding",
                            "${s(32)} ${s(56)} ${s(32)} ${s(40)}"
                        )
                    }
                    .sGap(36),
                // Both `CenterVertically` (cross-axis centring of items
                // in the flex row) and a stretched Box (so even the
                // taller of the two items has equal top/bottom space)
                // — previously the card was a fixed 682 tall and just
                // outran the panel, making "centring" a no-op since
                // there was no spare cross-axis. Card box is now smaller
                // than the panel so CenterVertically actually moves it.
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Center,
            ) {
                // Left: FIFA card preview. PlayerCard's `fillContainer`
                // mode hard-codes `top: 0` + `transform-origin: top
                // center` on its inner box — that's why the card kept
                // hugging the top of its wrapper. Render it at the
                // native Arena 94×147 and centre-scale manually with
                // `transform-origin: center`, wrapping the lot in a
                // flex-centred Box that's tall enough to host both the
                // scaled card AND extra vertical space, so the Row's
                // CenterVertically actually parks the card mid-panel.
                Box(
                    modifier = Modifier
                        .sWidth(300)
                        .sHeight(469)
                        .position(Position.Relative)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            it.stopPropagation()
                            if (!isSubmitting) onViewProfile(player.userId)
                        }
                        .styleModifier {
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("align-self", "center")
                        },
                ) {
                    val displayName = (player.fullName?.split(" ")?.lastOrNull()
                        ?: player.username).uppercase()
                    // Scale factor that maps the 94×147 design grid to
                    // the 300-wide / 469-tall slot. Width and height
                    // ratios are identical here (slot is built to the
                    // same 94:147 aspect) so a single scale suffices.
                    val cardScale = 300.0 / 94.0
                    Box(
                        modifier = Modifier
                            .sWidth(94).sHeight(147)
                            .position(Position.Relative)
                            .styleModifier {
                                property("transform", "scale($cardScale)")
                                property("transform-origin", "center center")
                            }
                    ) {
                        PlayerCard(
                            playerName = displayName,
                            rating = player.overallRating.toInt(),
                            position = player.position ?: "",
                            sport = sport,
                            size = PlayerCardSize.Arena,
                            avatarUrl = player.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                            cardAssetsBasePath = player.cardAssetsBasePath,
                            cardTextColor = player.cardTextColor,
                        )
                    }
                    // Top-right "★ 4.9" — sibling of the scale wrapper so
                    // its font size isn't blown up by the 3.83× transform.
                    Row(
                        modifier = Modifier
                            .position(Position.Absolute)
                            .sGap(4)
                            .styleModifier {
                                property("top", "16%")
                                property("right", "12%")
                                property("z-index", "5")
                                property("pointer-events", "none")
                                property("align-items", "center")
                            },
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Box(
                            modifier = Modifier
                                .sSize(28)
                                .styleModifier {
                                    property("background-color", STAR_GOLD)
                                    property("mask-image", "url('${AppIcons.StarRating16}')")
                                    property("-webkit-mask-image", "url('${AppIcons.StarRating16}')")
                                    property("mask-size", "contain")
                                    property("-webkit-mask-size", "contain")
                                    property("mask-position", "center")
                                    property("-webkit-mask-position", "center")
                                    property("mask-repeat", "no-repeat")
                                    property("-webkit-mask-repeat", "no-repeat")
                                }
                        )
                        // Digit wrapped in a 28-px-tall Box so its
                        // visual centre lines up with the 28×28 star
                        // box next to it — relying on the SpanText's
                        // own inline-flex centring left the glyph
                        // baseline-anchored and visibly low.
                        Box(
                            modifier = Modifier
                                .sHeight(28)
                                .styleModifier {
                                    property("display", "flex")
                                    property("align-items", "center")
                                },
                        ) {
                            SpanText(
                                communityAverage
                                    ?.let { (round(it * 10) / 10.0).toString() }
                                    ?: (round(player.overallRating * 10) / 10.0).toString(),
                                modifier = Modifier
                                    .sFontSize(28)
                                    .fontWeight(FontWeight.Normal)
                                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                                    .styleModifier {
                                        property("line-height", "1")
                                        property("font-family", "Urbanist, sans-serif")
                                    }
                            )
                        }
                    }
                }

                // Right: Rating panel — its own card with bg + border.
                Column(
                    modifier = Modifier
                        .sBorderRadius(18)
                        .styleModifier {
                            property("padding", "${s(16)} ${s(22)} ${s(22)} ${s(22)}")
                            property("background", PANEL_BG)
                            property("border", "0.5px solid $PANEL_BORDER")
                        }
                        .sGap(22),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    Column(modifier = Modifier.sGap(18), horizontalAlignment = Alignment.CenterHorizontally) {
                        // Header
                        Column(
                            modifier = Modifier.fillMaxWidth().sGap(2),
                            horizontalAlignment = Alignment.CenterHorizontally,
                        ) {
                            SpanText(
                                t.eventRatePerformanceTitle,
                                modifier = Modifier
                                    .sFontSize(20)
                                    .fontWeight(FontWeight.Medium)
                                    .sLineHeight(26)
                                    .color(com.arena_web.ui.theme.ThemeVars.Text1)
                                    .styleModifier {
                                        property("font-family", "Urbanist, sans-serif")
                                        property("letter-spacing", s(0.2))
                                        property("text-align", "center")
                                    }
                            )
                            SpanText(
                                t.eventRateVoteCounts,
                                modifier = Modifier
                                    .sFontSize(16)
                                    .fontWeight(FontWeight.Normal)
                                    .color(com.arena_web.ui.theme.ThemeVars.Text1)
                                    .styleModifier {
                                        property("line-height", "1.5")
                                        property("font-family", "Urbanist, sans-serif")
                                        property("letter-spacing", s(0.16))
                                        property("text-align", "center")
                                    }
                            )
                        }

                        // Attribute rows
                        Column(modifier = Modifier.sGap(8)) {
                            attributes.forEach { attr ->
                                AttributeRatingRow(
                                    label = attr.label,
                                    value = ratings.value[attr.attributeId] ?: 0,
                                    onChange = { newVal ->
                                        ratings.value = ratings.value.toMutableMap().apply {
                                            put(attr.attributeId, newVal)
                                        }
                                    },
                                )
                            }
                        }
                    }

                    // Community rating
                    Column(modifier = Modifier.fillMaxWidth().sGap(8)) {
                        SpanText(
                            t.eventCommunityRating,
                            modifier = Modifier
                                .sFontSize(16)
                                .fontWeight(FontWeight.Medium)
                                .sLineHeight(18)
                                .color(com.arena_web.ui.theme.ThemeVars.Text1)
                                .styleModifier {
                                    property("font-family", "Urbanist, sans-serif")
                                    property("letter-spacing", s(0.032))
                                }
                        )
                        Row(
                            modifier = Modifier.sGap(8),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Row(
                                modifier = Modifier.sGap(4),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                SpanText(
                                    communityAverage
                                        ?.let { (round(it * 10) / 10.0).toString() }
                                        ?: "—",
                                    modifier = Modifier
                                        .sFontSize(18)
                                        .fontWeight(FontWeight.Medium)
                                        .sLineHeight(22)
                                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                                        .styleModifier {
                                            property("font-family", "Urbanist, sans-serif")
                                            property("letter-spacing", s(0.09))
                                        }
                                )
                                Box(
                                    modifier = Modifier
                                        .sSize(24)
                                        .styleModifier {
                                            property("background-color", STAR_GOLD)
                                            property("mask-image", "url('${AppIcons.StarRating16}')")
                                            property("-webkit-mask-image", "url('${AppIcons.StarRating16}')")
                                            property("mask-size", "contain")
                                            property("-webkit-mask-size", "contain")
                                            property("mask-position", "center")
                                            property("-webkit-mask-position", "center")
                                            property("mask-repeat", "no-repeat")
                                            property("-webkit-mask-repeat", "no-repeat")
                                        }
                                )
                            }
                            SpanText(
                                "($communityCount)",
                                modifier = Modifier
                                    .sFontSize(16)
                                    .fontWeight(FontWeight.Light)
                                    .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
                                    .styleModifier {
                                        property("line-height", "1.5")
                                        property("font-family", "Urbanist, sans-serif")
                                        property("letter-spacing", s(0.032))
                                    }
                            )
                        }
                    }

                    // Submit Rating button — football blue pill. Hidden
                    // when the viewer is looking at their own card,
                    // because the backend forbids self-rating
                    // ("Cannot rate yourself"). The rest of the popup
                    // stays visible so the user can still see their
                    // own community rating + per-attribute scores.
                    if (!isSelf) {
                        Box(
                            modifier = Modifier
                                .sBorderRadius(22)
                                .styleModifier {
                                    property("padding", "${s(13)} ${s(26)}")
                                    property(
                                        "background",
                                        SportColors.forSport(sport).primary.toString(),
                                    )
                                }
                                .cursor(Cursor.Pointer)
                                .onClick {
                                    it.stopPropagation()
                                    if (!isSubmitting) onSubmit(ratings.value)
                                },
                            contentAlignment = Alignment.Center,
                        ) {
                            SpanText(
                                t.eventSubmitRating,
                                modifier = Modifier
                                    .sFontSize(16)
                                    .fontWeight(FontWeight.Medium)
                                    .sLineHeight(18)
                                    .color(AppColors.PrimaryWhite)
                                    .styleModifier {
                                        property("font-family", "Urbanist, sans-serif")
                                        property("letter-spacing", s(0.032))
                                        property("text-align", "center")
                                        property("white-space", "nowrap")
                                    }
                            )
                        }
                    }
                }
            }

            // Loading overlay — dims the whole popup surface while the
            // rate POST is in flight, centred on the project's standard
            // `LoadingSpinner`. Sits above all popup content with
            // `pointer-events: auto` so taps can't reach the underlying
            // submit / close handlers.
            if (isSubmitting) {
                Box(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .styleModifier {
                            property("top", "0")
                            property("left", "0")
                            property("right", "0")
                            property("bottom", "0")
                            property("background", "rgba(2, 4, 11, 0.65)")
                            property("backdrop-filter", "blur(2px)")
                            property("border-radius", s(18))
                            property("z-index", "10")
                        }
                        .onClick { it.stopPropagation() },
                    contentAlignment = Alignment.Center,
                ) {
                    com.arena_web.ui.components.widgets.LoadingSpinner(
                        size = 64.px,
                    )
                }
            }
        }
    }
}

/**
 * Mobile Rate Performance sheet (Figma 8697:98266) — the phone counterpart
 * of [RatePlayerPopup], rendered as a project [com.arena_web.ui.components.overlay.BottomSheet]:
 * card preview with the community ★, "Rate Performance / Your vote counts!"
 * headline, the six [AttributeRatingRow]s, the community-rating summary and
 * a full-width sport-tinted Submit button pinned at the bottom.
 *
 * Same contract as the popup: [initialRatings] pre-fills a re-vote,
 * [isSelf] hides the submit (backend rejects self-rating), [isSubmitting]
 * blocks dismissal and repeat-submits; the caller closes the sheet once the
 * POST resolves.
 */
@Composable
fun MobileRatePlayerSheet(
    player: EventParticipantDto,
    sport: String,
    initialRatings: Map<String, Int> = emptyMap(),
    communityAverage: Double? = null,
    communityCount: Int = 0,
    isSubmitting: Boolean = false,
    isSelf: Boolean = false,
    // Event stats snapshot — drives the read-only corner StatBlocks on the
    // card preview (goals/assists etc.), same as the Lineups grid cards.
    playerStats: com.arena_web.data.dto.EventPlayerStatsResultDto? = null,
    // Non-Pro events carry no structured scoring — the caller turns the
    // corner stat tiles off entirely.
    showStatBlocks: Boolean = true,
    // Tap on the card preview → the caller opens the profile (sheet on
    // phones). Same contract as the desktop popup's card tap.
    onViewProfile: (userId: String) -> Unit = {},
    onClose: () -> Unit,
    onSubmit: (Map<String, Int>) -> Unit,
) {
    val t = LocalAppStrings.current
    val attributes = remember(sport) { attributesForSport(sport) }
    var ratings by remember(player.userId, sport, initialRatings) {
        mutableStateOf(
            attributes.associate { attr ->
                attr.attributeId to (initialRatings[attr.attributeId] ?: 0)
            }
        )
    }
    val sportColors = com.arena_web.ui.theme.LocalSportColors.current

    com.arena_web.ui.components.overlay.BottomSheet(
        onDismiss = { if (!isSubmitting) onClose() },
        contentGap = 22,
    ) {
        // Scrollable body — card, headline, attribute rows, community block.
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .sGap(12)
                .styleModifier {
                    property("min-height", "0")
                    property("overflow-y", "auto")
                    property("scrollbar-width", "none")
                },
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            // Card preview (Figma 8697:98308 — 113×175). The desktop
            // `PlayerCardKit` renders at its intrinsic XtraSmall 58×90 and
            // CSS-scales up to fill the slot, bringing the read-only corner
            // StatBlocks (goals/assists…) and the ★ average with it — same
            // recipe as the mobile Lineups grid cards.
            Box(
                modifier = Modifier
                    .sWidth(113)
                    .sHeight(175)
                    .position(Position.Relative)
                    .styleModifier {
                        property("flex-shrink", "0")
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                    }
            ) {
                PlayerCardKit(
                    player = player,
                    cardSize = PlayerCardSize.XtraSmall,
                    cardScale = 113.0 / 58.0,
                    sport = sport,
                    showStatBlocks = showStatBlocks,
                    isAdmin = false,
                    playerStats = playerStats,
                    showRating = true,
                    hideZeroStats = true,
                    onPlayerClick = { if (!isSubmitting) onViewProfile(player.userId) },
                    eventRatingAverage = communityAverage,
                )
            }

            // Headline.
            Column(
                modifier = Modifier.sGap(2),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                SpanText(
                    t.eventRatePerformanceTitle,
                    modifier = Modifier
                        .sFontSize(20)
                        .fontWeight(FontWeight.Medium)
                        .sLineHeight(26)
                        .color(com.arena_web.ui.theme.ThemeVars.Text1)
                        .styleModifier {
                            property("letter-spacing", s(0.2))
                            property("text-align", "center")
                            property("font-family", "Urbanist, sans-serif")
                        },
                )
                SpanText(
                    t.eventRateVoteCounts,
                    modifier = Modifier
                        .sFontSize(16)
                        .sLineHeight(24)
                        .color(com.arena_web.ui.theme.ThemeVars.Text1)
                        .styleModifier {
                            property("letter-spacing", s(0.16))
                            property("text-align", "center")
                            property("font-family", "Urbanist, sans-serif")
                        },
                )
            }

            // Attribute stars.
            Column(modifier = Modifier.sGap(8).sMarginTop(6)) {
                attributes.forEach { attr ->
                    AttributeRatingRow(
                        label = attr.label,
                        value = ratings[attr.attributeId] ?: 0,
                        onChange = { v -> ratings = ratings + (attr.attributeId to v) },
                    )
                }
            }

            // Community rating summary.
            Column(modifier = Modifier.fillMaxWidth().sGap(8).sMarginTop(10)) {
                SpanText(
                    t.eventCommunityRating,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(FontWeight.Medium)
                        .sLineHeight(18)
                        .color(com.arena_web.ui.theme.ThemeVars.Text1)
                        .styleModifier {
                            property("letter-spacing", s(0.032))
                            property("font-family", "Urbanist, sans-serif")
                        },
                )
                Row(
                    modifier = Modifier.sGap(8),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Row(
                        modifier = Modifier.sGap(4),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        SpanText(
                            communityAverage
                                ?.let { (round(it * 10) / 10.0).toString() }
                                ?: "0.0",
                            modifier = Modifier
                                .sFontSize(18)
                                .fontWeight(FontWeight.Medium)
                                .sLineHeight(22)
                                .color(com.arena_web.ui.theme.ThemeVars.Text2)
                                .styleModifier {
                                    property("letter-spacing", s(0.09))
                                    property("font-family", "Urbanist, sans-serif")
                                },
                        )
                        Box(
                            modifier = Modifier
                                .sSize(24)
                                .styleModifier {
                                    property("background-color", STAR_GOLD)
                                    property("mask-image", "url('${AppIcons.StarRating16}')")
                                    property("-webkit-mask-image", "url('${AppIcons.StarRating16}')")
                                    property("mask-size", "contain")
                                    property("-webkit-mask-size", "contain")
                                    property("mask-position", "center")
                                    property("-webkit-mask-position", "center")
                                    property("mask-repeat", "no-repeat")
                                    property("-webkit-mask-repeat", "no-repeat")
                                }
                        )
                    }
                    SpanText(
                        "($communityCount)",
                        modifier = Modifier
                            .sFontSize(16)
                            .sLineHeight(24)
                            .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x99, 0xB1, 0xBF))
                            .styleModifier {
                                property("font-weight", "300")
                                property("letter-spacing", s(0.032))
                                property("font-family", "Urbanist, sans-serif")
                            },
                    )
                }
            }
        }

        // Submit — pinned under the scroll body. Hidden on your own card
        // (backend rejects self-rating), busy label while the POST runs.
        if (!isSelf) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .sHeight(44)
                    .sBorderRadius(22)
                    .backgroundColor(sportColors.primary)
                    .cursor(Cursor.Pointer)
                    .styleModifier {
                        property("flex-shrink", "0")
                        if (isSubmitting) property("opacity", "0.6")
                    }
                    .onClick { if (!isSubmitting) onSubmit(ratings) },
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    if (isSubmitting) t.eventSubmitting else t.eventSubmitRating,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(FontWeight.Medium)
                        .sLineHeight(18)
                        .color(sportColors.onPrimary)
                        .styleModifier {
                            property("letter-spacing", s(0.032))
                            property("font-family", "Urbanist, sans-serif")
                        },
                )
            }
        }
    }
}

@Composable
private fun AttributeRatingRow(
    label: String,
    value: Int,
    onChange: (Int) -> Unit,
) {
    Row(
        modifier = Modifier.sGap(16),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            label,
            modifier = Modifier
                .sWidth(38)
                .sFontSize(18)
                .fontWeight(FontWeight.Medium)
                .sLineHeight(22)
                .color(com.arena_web.ui.theme.ThemeVars.Text1)
                .styleModifier {
                    property("font-family", "Urbanist, sans-serif")
                    property("letter-spacing", s(0.09))
                }
        )
        // Each star sits in a 44×44 tap target (Figma 8691:235577 frame
        // — `size-[44px]` wraps `size-[24px]` star), with 4-px gap.
        Row(modifier = Modifier.sGap(4)) {
            for (i in 1..5) {
                val filled = i <= value
                val maskUrl = if (filled) AppIcons.StarRating16 else AppIcons.StarOutline
                Box(
                    modifier = Modifier
                        .sSize(44)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            it.stopPropagation()
                            onChange(if (i == value) i - 1 else i)
                        },
                    contentAlignment = Alignment.Center,
                ) {
                    Box(
                        modifier = Modifier
                            .sSize(24)
                            .styleModifier {
                                property("background-color", STAR_GOLD)
                                property("mask-image", "url('$maskUrl')")
                                property("-webkit-mask-image", "url('$maskUrl')")
                                property("mask-size", "contain")
                                property("-webkit-mask-size", "contain")
                                property("mask-position", "center")
                                property("-webkit-mask-position", "center")
                                property("mask-repeat", "no-repeat")
                                property("-webkit-mask-repeat", "no-repeat")
                            }
                    )
                }
            }
        }
    }
}
